import { HttpClient } from '@adapters/HttpClient';
import type { Order } from '@modules/OrderPreparation/PackingV2/models/Packing';
import type { PreparationPickingOrdersResponse } from '@modules/OrderPreparation/PackingV2/models/Packing';
import type { AdjustPickingDiscrepancyPayload, AdjustPickingDiscrepancyResponse, Preparation } from '../models';

// Interfaces para las peticiones y respuestas
export interface AssignCartRequest {
  cart_code: string;
}

export interface AssignCartResponse {
  success: boolean;
  message: string;
  preparation?: Preparation;
}

export interface CartResponse {
  data: {
    id: number;
    team_id: number;
    code: string;
    type_id: number;
    type: {
      id: number;
      name: string;
    };
    is_locked: boolean;
    product_id: number | null;
    product: any[];
    deleted_at: string | null;
    stock: number | null;
    stock_location_carts: any[];
  };
}

export interface PickingRequest {
  product_id: number;
  qty: number;
  location_id: number;
  batch_id?: number;
}

export interface PickingResponse {
  success: boolean;
  message: string;
  preparation?: Preparation;
}

export interface CancelResponse {
  success: boolean;
  message: string;
  preparation?: Preparation;
}

export class PickingApiAdapter {
  private static readonly BASE_URL = '/preparations';

  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene una preparación por ID
   */
  async getPreparation(id: number): Promise<Preparation> {
    return await this.httpClient.get<Preparation>(`${PickingApiAdapter.BASE_URL}/${id}`);
  }

  /**
   * Asigna un carrito a una preparación
   */
  async assignCart(preparationId: number, cartCode: string): Promise<AssignCartResponse> {
    try {
      const requestBody: AssignCartRequest = {
        cart_code: cartCode,
      };

      const response = await this.httpClient.put<any>(
        `${PickingApiAdapter.BASE_URL}/picking/${preparationId}/cart`,
        requestBody,
      );

      return {
        success: true,
        message: `Carrito "${cartCode}" validado correctamente`,
        preparation: response.preparation,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          'Error al asignar el carrito',
      };
    }
  }

  /**
   * Obtiene información de un carrito por ID
   */
  async getCart(cartId: number): Promise<{ success: boolean; code?: string; message: string }> {
    try {
      const response = await this.httpClient.get<CartResponse>(`/carts/${cartId}`);

      return {
        success: true,
        code: response.data.code,
        message: 'Carrito obtenido correctamente',
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          'Error al obtener el carrito',
      };
    }
  }

  /**
   * Confirma el picking de un producto
   */
  async confirmPicking(
    preparationId: number,
    productId: number,
    quantity: number,
    locationId: number,
    batchId?: number,
  ): Promise<PickingResponse> {
    try {
      const requestBody: PickingRequest = {
        product_id: productId,
        qty: quantity,
        location_id: locationId,
        ...(batchId && { batch_id: batchId }),
      };

      const response = await this.httpClient.put<any>(
        `${PickingApiAdapter.BASE_URL}/picking/${preparationId}/pick`,
        requestBody,
      );

      return {
        success: true,
        message: `Picking confirmado: ${quantity} unidades del producto`,
        preparation: response, // El backend devuelve directamente la preparación actualizada
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          'Error al confirmar el picking',
      };
    }
  }

  /**
   * Lista pedidos asociados a una preparación de picking
   */
  async getPickingOrders(preparationId: number): Promise<Order[]> {
    const response = await this.httpClient.get<PreparationPickingOrdersResponse>(
      `${PickingApiAdapter.BASE_URL}/picking/${preparationId}/orders`,
    );
    return response.data ?? [];
  }

  /**
   * Previsualiza el ajuste de descuadre (dry-run, sin persistir)
   */
  async previewAdjustDiscrepancy(
    preparationId: number,
    payload: AdjustPickingDiscrepancyPayload,
  ): Promise<AdjustPickingDiscrepancyResponse> {
    return await this.httpClient.post<AdjustPickingDiscrepancyResponse>(
      `${PickingApiAdapter.BASE_URL}/picking/${preparationId}/adjust-discrepancy/preview`,
      payload,
    );
  }

  /**
   * Aplica el ajuste de descuadre
   */
  async applyAdjustDiscrepancy(
    preparationId: number,
    payload: AdjustPickingDiscrepancyPayload,
  ): Promise<AdjustPickingDiscrepancyResponse> {
    return await this.httpClient.post<AdjustPickingDiscrepancyResponse>(
      `${PickingApiAdapter.BASE_URL}/picking/${preparationId}/adjust-discrepancy`,
      payload,
    );
  }

  /**
   * Cancela una preparación de picking
   */
  async cancelPicking(preparationId: number): Promise<CancelResponse> {
    try {
      await this.httpClient.put<any>(`${PickingApiAdapter.BASE_URL}/${preparationId}/cancel`, {});

      // El endpoint responde 204 (No Content) cuando se cancela exitosamente
      return {
        success: true,
        message: 'Picking cancelado correctamente',
        // No hay preparation en la respuesta 204, se manejará en el componente
        preparation: undefined,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          'Error al cancelar el picking',
      };
    }
  }
}
